import { invariant, uuid } from "@banjoanton/utils";
import {
    type AsyncResultType,
    createLogger,
    ErrorUtil,
    Result,
    TrackingService,
    ValidationUtil,
} from "@chesspresso/common";
import { ChessGameCollectionRepository } from "../../../db/chess-game-collection-repository";
import { ChessGameRepository } from "../../../db/chess-game-repository";
import { CollectionStatsRepository } from "../../../db/collection-stats-repository";
import { UserRepository } from "../../../db/user-repository";
import { ChessGameCollection } from "../../../models/chess-game-collection";
import { ChessGame } from "../../../models/chess-game-model";

import { User } from "../../../models/user-model";
import { StatsService } from "../../../services/stats-service";
import {
    type ChessComArchive,
    type ChessComRawGame,
    ChessComRawGamesSchema,
    type ChessComStats,
    ChessComStatsSchema,
} from "../models/chess-com-models";
import {
    type EnrichGamesWithOpeningsProps,
    type OpeningIndex,
    OpeningService,
} from "../../../services/opening-service";
import pMap from "p-map";

const logger = createLogger("chess-com-service");

const BASE_URL = "https://api.chess.com";
const PLAYER_ENDPOINT = (player: string, subpath: string) =>
    `${BASE_URL}/pub/player/${player.toLowerCase()}/${subpath}`;

const fetchPlayerStats = async (
    player: string
): AsyncResultType<ChessComStats> => {
    logger.debug({ player }, "Fetching player stats from Chess.com");

    let response;
    try {
        response = await fetch(PLAYER_ENDPOINT(player, "stats"), {
            method: "GET",
        });
    } catch (error) {
        TrackingService.captureError(error);
        const message = ErrorUtil.toErrorMessage(error);
        logger.error({ player, error }, "Failed to fetch player stats");
        return Result.error(
            `Could not fetch player stats: ${message}`,
            "INTERNAL_SERVER_ERROR"
        );
    }

    const data = await response.json();
    const stats = ChessComStatsSchema.safeParse(data);

    if (!stats.success) {
        const message = ValidationUtil.errorToString(stats.error);
        logger.error({ player, message }, "Failed to parse player stats");
        return Result.error(`Invalid data format: ${message}`, "BAD_REQUEST");
    }

    return Result.ok(stats.data);
};

const fetchGameArchives = async (player: string) => {
    logger.debug({ player }, "Fetching game archives from Chess.com");
    const url = PLAYER_ENDPOINT(player, "games/archives");
    const response = await fetch(url, { method: "GET" });
    return (await response.json()) as ChessComArchive;
};

type FetchAllGamesProps = {
    player: string;
    timestampSeconds?: number;
    collectionId?: string;
    onProgress?: (progress: number) => void;
    openingIndex?: OpeningIndex;
};

// TODO: we need to make this more robust in error handling, use the result type maybe
export const fetchAllGames = async ({
    player,
    timestampSeconds,
    collectionId = uuid(),
    onProgress,
    openingIndex,
}: FetchAllGamesProps) => {
    logger.debug(
        { player, timestamp: timestampSeconds },
        "Fetching all games from Chess.com archives"
    );

    const timestamp = timestampSeconds ? new Date(timestampSeconds * 1000) : 0;

    const timestampDate = new Date(timestamp);
    const approximateDate = new Date(
        timestampDate.getFullYear(),
        timestampDate.getMonth(),
        1
    );
    logger.debug(
        { player, approximateDate },
        "Using approximate date for filtering archives"
    );
    const archives = await fetchGameArchives(player);
    const filteredArchives = archives.archives.filter((archive) => {
        const [year, month] = archive.split("/").slice(-2);
        if (!year || !month) {
            return false;
        }
        const archiveDate = new Date(
            Number.parseInt(year),
            Number.parseInt(month) - 1
        );
        return archiveDate >= approximateDate;
    });

    logger.debug(
        { count: filteredArchives.length },
        `Found ${filteredArchives.length} archives after filtering`
    );

    const allGames: ChessComRawGame[] = [];
    let completed = 0;
    const archiveResults = await pMap(
        filteredArchives,
        async (archive) => {
            try {
                const response = await fetch(archive, { method: "GET" });
                const games = ChessComRawGamesSchema.safeParse(
                    await response.json()
                );
                if (!games.success) {
                    const message = ValidationUtil.errorToString(games.error);
                    TrackingService.captureError(message);
                    logger.error(
                        { archive, message },
                        "Failed to parse games from archive"
                    );
                    return [];
                }
                logger.debug(
                    { count: games.data.games.length },
                    `Fetched ${games.data.games.length} games from archive ${archive}`
                );
                return games.data.games.filter(
                    (rawGame) => rawGame.rules === "chess"
                );
            } catch (error) {
                TrackingService.captureError(error);
                logger.error({ archive, error }, "Failed to fetch archive");
                return [];
            } finally {
                completed += 1;
                const progress = completed / filteredArchives.length;
                onProgress?.(progress * 0.5); // fetching only accounts for 50% of progress
            }
        },
        { concurrency: 3 }
    );
    for (const games of archiveResults) {
        allGames.push(...games);
    }

    logger.debug(
        { count: allGames.length },
        `Total games fetched: ${allGames.length}`
    );

    const firstGame = allGames[0];
    invariant(firstGame, "First game doesn't exist");

    const playerName =
        firstGame.white.username.toLowerCase() === player.toLowerCase()
            ? firstGame.white.username
            : firstGame.black.username;

    const enrichmentOnProgress = onProgress
        ? (progress: number) => onProgress(0.5 + progress * 0.5) // the opening calcs are the other 50%
        : // the opening calcs are the other 50%
          undefined;

    const options: EnrichGamesWithOpeningsProps = {
        allGames,
        playerName,
        collectionId,
        onProgress: enrichmentOnProgress,
        openingIndex,
    };

    return OpeningService.enrichGamesWithOpenings(options);
};

type FetchAllGamesResult = {
    games: ChessGame[];
    collection: ChessGameCollection;
};

const fetchAndSaveAllGames = async (
    player: string,
    onProgress?: (progress: number) => void
): AsyncResultType<FetchAllGamesResult> => {
    const currentCollection =
        await ChessGameCollectionRepository.getByUsernameAndSource(
            player,
            "chess.com"
        );

    if (currentCollection) {
        logger.debug(
            { player, collectionId: currentCollection.id },
            "Found existing game collection, updating games"
        );

        // TODO: handle updating existing games
        return Result.error(
            "Updating existing collections is not implemented yet",
            "NOT_IMPLEMENTED"
        );
    }

    const collectionId = uuid();
    const games = await fetchAllGames({ player, collectionId, onProgress });
    const collection = ChessGameCollection.from({
        username: player,
        source: "chess.com",
        id: collectionId,
    });

    const currentUser = await UserRepository.get();

    if (!currentUser) {
        const user = User.from({
            username: player,
            collectionId,
            lastSync: Date.now(),
            createdAt: Date.now(),
        });

        await UserRepository.set(user);
    }

    await ChessGameCollectionRepository.add(collection);
    await ChessGameRepository.addAll(games);

    const stats = StatsService.calculateStats(games, collectionId);
    await CollectionStatsRepository.save(stats);

    const result = {
        games,
        collection,
    };

    return Result.ok(result);
};

const getSavedGames = async (
    collectionId: string
): AsyncResultType<ChessGame[]> => {
    logger.debug({ collectionId }, "Fetching saved games from Chess.com");
    const games = await ChessGameRepository.getByCollection(collectionId);

    if (games.length === 0) {
        logger.warn(
            { collectionId },
            "No saved games found for the given collection"
        );
        return Result.error("No saved games found", "NOT_FOUND");
    }

    return Result.ok(games);
};

type FetchAndSaveLatestGamesProps = {
    player: string;
    mostRecentGame: ChessGame;
    collectionId: string;
    onProgress?: (progress: number) => void;
};

const fetchAndSaveLatestGames = async ({
    player,
    mostRecentGame,
    collectionId,
    onProgress,
}: FetchAndSaveLatestGamesProps): AsyncResultType<ChessGame[]> => {
    logger.debug(
        { player, mostRecentGame, collectionId },
        "Fetching and saving latest games from Chess.com"
    );

    const allGames = await fetchAllGames({
        player,
        timestampSeconds: mostRecentGame.endTimeUnix,
        collectionId,
        onProgress,
    });

    const newGames = allGames.filter(
        (game) => game.endTimeUnix > mostRecentGame.endTimeUnix
    );

    if (newGames.length === 0) {
        logger.info(
            { player },
            "No new games found since the last known timestamp"
        );
        return Result.ok([]);
    }

    await ChessGameRepository.addAll(newGames);
    await ChessGameCollectionRepository.updateSyncTime(
        collectionId,
        Date.now()
    );

    const allStoredGames =
        await ChessGameRepository.getByCollection(collectionId);
    const stats = StatsService.calculateStats(allStoredGames, collectionId);
    await CollectionStatsRepository.save(stats);

    logger.info(
        { player, newGameCount: newGames.length },
        `Saved ${newGames.length} new games to the database`
    );

    return Result.ok(newGames);
};

export const ChessComService = {
    fetchPlayerStats,
    fetchAllGames,
    fetchAndSaveAllGames,
    fetchAndSaveLatestGames,
    getSavedGames,
};
